'use client'

import { useState, useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { X, Download, Smartphone } from 'lucide-react'
import { cn } from '@/lib/utils'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showBanner, setShowBanner] = useState(false)
  const [isIOS, setIsIOS] = useState(false)
  const [showIOSGuide, setShowIOSGuide] = useState(false)
  const pathname = usePathname()
  const isBackOffice = !!pathname && (pathname.startsWith('/admin') || pathname.startsWith('/cs'))
  // A merchant's customer mid-payment must never be asked to install FluxPay — and the
  // banner sat on top of the checkout's pay button and the result page's actions.
  const isCustomerPaymentPage = !!pathname && pathname.startsWith('/payment')

  useEffect(() => {
    // Never let a production PWA cache pin stale Next.js chunks during local
    // development. This also cleans up workers left by an earlier local run.
    if ('serviceWorker' in navigator) {
      if (process.env.NODE_ENV === 'production') {
        navigator.serviceWorker.register('/sw.js').catch(() => {})
      } else {
        navigator.serviceWorker.getRegistrations().then((registrations) =>
          Promise.all(registrations.map((registration) => registration.unregister()))
        ).catch(() => {})
        if ('caches' in window) {
          caches.keys().then((keys) =>
            Promise.all(keys.filter((key) => key.startsWith('fluxpay-')).map((key) => caches.delete(key)))
          ).catch(() => {})
        }
      }
    }

    // Check if already installed (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches) return

    // Check if dismissed recently (24h cooldown)
    const dismissed = localStorage.getItem('pwa-install-dismissed')
    if (dismissed && Date.now() - parseInt(dismissed) < 24 * 60 * 60 * 1000) return

    // iOS detection — no beforeinstallprompt, show manual guide
    const ua = navigator.userAgent
    const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    setIsIOS(ios)

    if (ios) {
      // Show iOS guide after 3 seconds
      const timer = setTimeout(() => setShowBanner(true), 3000)
      return () => clearTimeout(timer)
    }

    // Android/Chrome — listen for beforeinstallprompt
    const handler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setTimeout(() => setShowBanner(true), 2000)
    }

    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  const handleInstall = async () => {
    if (!deferredPrompt) return
    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') {
      setShowBanner(false)
    }
    setDeferredPrompt(null)
  }

  const handleDismiss = () => {
    setShowBanner(false)
    localStorage.setItem('pwa-install-dismissed', Date.now().toString())
  }

  // Suppress the "add to home screen" nudge on the staff back-office (admin/CS):
  // it's a merchant/customer-facing prompt and, being a body-level fixed banner,
  // it overlaps inline modals and bottom-of-page actions there.
  if (isBackOffice || isCustomerPaymentPage) return null
  if (!showBanner) return null

  // iOS guide
  if (isIOS) {
    return (
      <div className="fixed bottom-4 left-4 right-4 z-40 animate-in slide-in-from-bottom-4 duration-300">
        <div className="rounded-2xl bg-card/95 backdrop-blur-xl border border-border p-4 shadow-2xl shadow-black/40">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-fluxCyan-500 to-fluxCyan-600 flex items-center justify-center">
              <Smartphone className="h-5 w-5 text-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground">Install FluxPay</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Tap the share button <span className="inline-block align-middle text-fluxCyan-400">&#xfe0e;&#x2191;</span> then &quot;Add to Home Screen&quot;
              </p>
            </div>
            <button onClick={handleDismiss} className="flex-shrink-0 p-1 text-muted-foreground hover:text-muted-foreground">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Android/Chrome install banner
  return (
    <div className="fixed bottom-4 left-4 right-4 z-40 animate-in slide-in-from-bottom-4 duration-300">
      <div className="rounded-2xl bg-card/95 backdrop-blur-xl border border-border p-4 shadow-2xl shadow-black/40">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-fluxCyan-500 to-fluxCyan-600 flex items-center justify-center">
            <Download className="h-5 w-5 text-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">Install FluxPay</p>
            <p className="text-xs text-muted-foreground mt-0.5">Quick access from your home screen</p>
          </div>
          <button
            onClick={handleInstall}
            className="flex-shrink-0 px-4 py-2 rounded-xl bg-gradient-to-r from-fluxCyan-500 to-fluxCyan-600 text-white text-sm font-semibold hover:from-fluxCyan-400 hover:to-fluxCyan-500 transition-all"
          >
            Install
          </button>
          <button onClick={handleDismiss} className="flex-shrink-0 p-1 text-muted-foreground hover:text-muted-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
